import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import useStore from './store/useStore';
import { syncProfilesFromServer } from './utils/connectorConfig';

// Layout
import Layout from './components/Layout/Layout';
import LoadingScreen from './components/Common/LoadingScreen';
import Chatbot from './components/Common/Chatbot';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import ConsumerDashboard from './pages/ConsumerDashboard';
import Assets from './pages/Assets';
import Policies from './pages/Policies';
import DataExchange from './pages/DataExchange';
import ProviderDataExchange from './pages/ProviderDataExchange';
import ConsumerDataExchange from './pages/ConsumerDataExchange';
import TestPolicyCreation from './pages/TestPolicyCreation';
import DataStore from './pages/DataStore';
import Settings from './pages/Settings';
import DataPool from './pages/DataPool';
import Contracts from './pages/Contracts';

// Protected Route Component
function ProtectedRoute({ children }) {
  const isAuthenticated = useStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Admin-only Route Component
function AdminRoute({ children }) {
  const { isAuthenticated, user } = useStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('realm-admin');
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
}

function App() {
  const [loading, setLoading] = useState(true);
  const { setUser, isAuthenticated, theme, incrementConnectorKey } = useStore();

  // Apply theme class to <html> on mount and when theme changes
  useEffect(() => {
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  }, [theme]);

  useEffect(() => {
    // One-time migration: clear all connectorConfig_* keys that may have been
    // created by an earlier buggy version (which saved flat keys on logout,
    // potentially with another user's data). Version '2' = user-scoped reads.
    if (localStorage.getItem('connectorConfigVersion') !== '2') {
      Object.keys(localStorage)
        .filter(k => k.startsWith('connectorConfig_'))
        .forEach(k => localStorage.removeItem(k));
      localStorage.setItem('connectorConfigVersion', '2');
    }

    // Check for existing token
    const token = localStorage.getItem('kc_token');
    const username = localStorage.getItem('username');
    const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');

    if (token && username) {
      setUser({ username, roles: userRoles });
      // Refresh this browser's connector cache from the account's saved
      // state (e.g. on a page reload where Login.jsx's own sync never ran).
      // Awaited, and followed by incrementConnectorKey(), for the same
      // reason as Login.jsx: pages memoize their active connector on that
      // key and won't pick up a sync that finished after their first
      // render otherwise.
      syncProfilesFromServer().finally(() => {
        incrementConnectorKey();
        setLoading(false);
      });
      return;
    }

    setLoading(false);
  }, [setUser]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />}
        />

        {/* Protected Routes */}
        <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />

        <Route path="/dashboard" element={
          <ProtectedRoute><Dashboard /></ProtectedRoute>
        } />

        <Route path="/provider-dashboard" element={
          <ProtectedRoute><ProviderDashboard /></ProtectedRoute>
        } />

        <Route path="/consumer-dashboard" element={
          <ProtectedRoute><ConsumerDashboard /></ProtectedRoute>
        } />

        <Route path="/assets" element={
          <ProtectedRoute><Assets /></ProtectedRoute>
        } />

        <Route path="/policies" element={
          <ProtectedRoute><Policies /></ProtectedRoute>
        } />

        <Route path="/data-exchange" element={
          <ProtectedRoute><DataExchange /></ProtectedRoute>
        } />

        <Route path="/provider-data-exchange" element={
          <ProtectedRoute><ProviderDataExchange /></ProtectedRoute>
        } />

        <Route path="/consumer-data-exchange" element={
          <ProtectedRoute><ConsumerDataExchange /></ProtectedRoute>
        } />

        <Route path="/test-policy" element={
          <ProtectedRoute><TestPolicyCreation /></ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute><Settings /></ProtectedRoute>
        } />

        <Route path="/data-pool" element={
          <ProtectedRoute><DataPool /></ProtectedRoute>
        } />

        <Route path="/contracts" element={
          <ProtectedRoute><Contracts /></ProtectedRoute>
        } />

        {/* Admin-only Route */}
        <Route path="/data-store" element={
          <AdminRoute><DataStore /></AdminRoute>
        } />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {isAuthenticated && <Chatbot />}
    </Router>
  );
}

export default App;
